/**
 * BimonadExplainer — progressive-disclosure explainer for bimonads, with
 * emphasis on the *graded* bimonad that pairs PRECOMMIT (graded monad) and
 * REQUIRE (graded comonad) under a distributive law.
 *
 * A bimonad is one carrier that is simultaneously a monad and a comonad,
 * with a distributive law `χ : T ∘ W ⇒ W ∘ T` reconciling the two. CSK uses
 * the graded version to make "commitment-issuing" and "obligation-discharging"
 * compose as a single record — the structural anchor of `ICG-At`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  GRAY,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  AdjunctionToMonadDiagram,
  ComonadDiagram,
  ExplainerCard,
  MonadDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const MONAD_ACCENT = THEOREM_COLOR.PRECOMMIT;
const COMONAD_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-bimonad",
  kind: "Categorical structure",
  title: "Bimonad",
  subtitle: "(T, η, μ) ⊗ (W, ε, δ) with distributive law χ : T ∘ W ⇒ W ∘ T",
  accent: MONAD_ACCENT,
  accentAlt: COMONAD_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Monad", href: "#explainer-monad", accent: MONAD_ACCENT },
  { label: "Comonad", href: "#explainer-comonad", accent: COMONAD_ACCENT },
  { label: "Graded monad", href: "#explainer-graded-monad", accent: MONAD_ACCENT },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "Bialgebra (algebra version)", href: "#explainer-bialgebra" },
  { label: "PRECOMMIT ⊣ REQUIRE", href: "#explainer-bimonad", accent: COMONAD_ACCENT },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>bimonad</strong> is one carrier worn as both a monad <em>and</em> a comonad,
      coherent under a distributive law. CSK's <strong>graded bimonad</strong> pairs a
      commitment-tracking monad (<code>PRECOMMIT</code>) with an obligation-discharging comonad (
      <code>REQUIRE</code>) in a single record — so "what was committed" and "what is still owed"
      can never disagree.
    </p>
  ),
  intuition: (
    <>
      <p>
        A monad asks: <em>"what effect will I leave behind?"</em> A comonad asks:{" "}
        <em>"what context do I need to run?"</em> Most programs live in both directions: a function
        both consumes evidence and produces effects. A <strong>bimonad</strong> says: do not split
        these into two unrelated structures. Pair them, and ask one additional thing — that they be{" "}
        <em>distributive</em>: pushing effects past requirements is the same as pulling requirements
        past effects.
      </p>
      <p>
        The everyday analogy is a kitchen ticket. The monad part is the chef's <em>memory</em> of
        what was ordered (a growing list of commitments). The comonad part is the diner's{" "}
        <em>expectation</em> of what is still owed (a growing list of obligations). The distributive
        law is the rule that says: if a commitment is made and an obligation is later discharged,
        the order in which we account for them does not matter — the bill is the same.
      </p>
      <p>
        Without that law, the chef and the diner can drift apart. With it, they are two views of one
        event stream.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`C`}</MathInline> be a category. A <strong>bimonad</strong> on{" "}
        <MathInline>{tex`C`}</MathInline> is a tuple
      </p>
      <MathBlock ariaLabel="(T, η, μ, W, ε, δ, χ)">
        {tex`(T, \eta, \mu, W, \varepsilon, \delta, \chi)`}
      </MathBlock>
      <p>
        where <MathInline>{tex`(T, \eta, \mu)`}</MathInline> is a monad on{" "}
        <MathInline>{tex`C`}</MathInline>, <MathInline>{tex`(W, \varepsilon, \delta)`}</MathInline>{" "}
        is a comonad on <MathInline>{tex`C`}</MathInline>, and{" "}
        <MathInline>{tex`\chi : T \circ W \Rightarrow W \circ T`}</MathInline> is a{" "}
        <strong>distributive law</strong> compatible with the two units, two multiplications, and
        two counits. Compatibility is the standard pair of pentagons and the four bialgebra-style
        coherence squares.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem",
          alignItems: "start",
          marginTop: "0.5rem",
        }}
      >
        <div>
          <MonadDiagram accent={MONAD_ACCENT} effect="T" />
          <p
            style={{
              marginTop: "0.25rem",
              textAlign: "center",
              fontSize: "0.75rem",
              color: GRAY[600],
            }}
          >
            <strong style={{ color: MONAD_ACCENT }}>monad side</strong> · effects accumulate
          </p>
        </div>
        <div>
          <ComonadDiagram accent={COMONAD_ACCENT} effect="W" />
          <p
            style={{
              marginTop: "0.25rem",
              textAlign: "center",
              fontSize: "0.75rem",
              color: GRAY[600],
            }}
          >
            <strong style={{ color: COMONAD_ACCENT }}>comonad side</strong> · requirements
          </p>
        </div>
      </div>
      <p style={{ marginTop: "0.75rem" }}>
        <strong>Graded bimonad.</strong> Replace each side with its graded version: the monad
        becomes <MathInline>{tex`T_{\kappa}`}</MathInline> indexed by a commitment grade monoid, the
        comonad becomes <MathInline>{tex`W_{\lambda}`}</MathInline> indexed by an obligation grade
        monoid, and the distributive law becomes a family
      </p>
      <MathBlock ariaLabel="χ_{k,ℓ} : T_k ∘ W_ℓ ⇒ W_ℓ ∘ T_k">
        {tex`\chi_{k,\ell} : T_{k} \circ W_{\ell} \;\Rightarrow\; W_{\ell} \circ T_{k}`}
      </MathBlock>
      <p>
        natural in the grade indices. This is exactly the structure mechanized in CSK's{" "}
        <code>GradedBimonad</code> record (MathLibCore), specialised for PRECOMMIT (W) and REQUIRE
        (At) in the temporal-generator unification research line.
      </p>
      <p style={{ marginTop: "0.75rem" }}>
        Every <strong>adjunction</strong> <MathInline>{tex`F \dashv G`}</MathInline> already
        produces both a monad and a comonad — that is the source of the bimonad. The graded version
        adds a grade monoid that both sides agree to track.
      </p>
      <AdjunctionToMonadDiagram accent={MONAD_ACCENT} />
    </>
  ),
  relevance: (
    <>
      <p>
        Bimonads — and especially graded bimonads — are the structural mechanism through which CSK
        records the joint discipline of <strong>PRECOMMIT</strong> and <strong>REQUIRE</strong>. The
        pair is canon as <code>ICG-At</code>: the Immunity Closure Guarantee that fuses a
        Neutralization Theorem with its Structural Duality Principle into a single two-sided
        certificate.
      </p>
      <ul>
        <li>
          <strong>PRECOMMIT side (monad).</strong> Each commitment is a <em>graded Kleisli step</em>
          : "I will discharge this within grade <MathInline>{tex`k`}</MathInline>." Composition
          accumulates grades; no step can lower them.
        </li>
        <li>
          <strong>REQUIRE side (comonad).</strong> Each obligation is a{" "}
          <em>graded co-Kleisli step</em>: "I need this evidence at grade{" "}
          <MathInline>{tex`\ell`}</MathInline> in hand." Composition tracks requirements; no step
          can silently drop one.
        </li>
        <li>
          <strong>
            Distributive law <MathInline>{tex`\chi`}</MathInline>.
          </strong>{" "}
          The structural witness that pushing a commitment past an obligation produces the same end
          state as pulling the obligation past the commitment. When{" "}
          <MathInline>{tex`\chi`}</MathInline> does not exist for a candidate pair, the joint
          envelope is <em>structurally inexpressible</em> — the kernel rejects the program at
          construction time, not at audit time.
        </li>
        <li>
          <strong>Yang–Baxter coherence.</strong> When three sides interact (commitment · obligation
          · commitment, say), the distributive laws must satisfy a hexagon — the Yang–Baxter
          equation. CSK's research line names this as the integrability witness for the bimonad
          pair.
        </li>
      </ul>
      <p>
        Read in this light, a bimonad is the smallest structure that lets a program assert "I am
        making a commitment <em>and</em> I am consuming an obligation" — and have the type system
        certify that the two stories stay coherent under every composition.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Section on graded bimonad construction (PRECOMMIT × REQUIRE with Yang–Baxter coherence).",
    },
    {
      label: "GradedBimonad (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/GradedBimonad.agda",
      note: "Canonical record: grading monoid + graded monad + graded comonad + coherence slot.",
    },
    {
      label: "GradedBiMonad research module (Agda)",
      path: "agda/Research/TemporalGeneratorUnification/GradedBiMonad.agda",
      note: "Research formulation unifying PRECOMMIT (W) and REQUIRE (At).",
    },
    {
      label: "REQUIRE comonad duality (Kernel, Agda)",
      path: "agda/Kernel/CompositionalityPrinciple/REQUIRE/ComonadDuality.agda",
    },
    {
      label: "Theorem Canon (ICG-At)",
      path: "docs/reference/Theorem-Canon.md",
      note: "Where the PRECOMMIT ⊣ REQUIRE Immunity Closure Guarantee is canonized.",
    },
  ],
};

export function BimonadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<AdjunctionToMonadDiagram accent={MONAD_ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
